import { useEffect, useState } from "react";
import type { RunBundle } from "@picnic-field/local-host/protocol";
import { relayMotion } from "../lib/relayMotion";
import type { RelayMotion } from "../lib/relayMotion";

/**
 * 스냅샷들의 릴레이 움직임 판정(SR-MODE-07 부기 2026-09-30) — run-bundle `program.ast` 를 받아 {@link relayMotion}.
 *
 * 스냅샷은 바뀌지 않으므로 판정도 바뀌지 않는다 — 한 번 받은 것은 모듈 안에 남긴다(1초 폴링 · 다시 그리기마다 다시 받지 않는다).
 * 못 받으면(권한 · 교류 룸의 다른 반 스냅샷 · 네트워크) `unknown` — 경고하지 않는다. `on` 이 거짓이면(Setup 이 아니면) 받지 않는다.
 */
const cache = new Map<string, Promise<RelayMotion>>();

/** 시험용 — 모듈 캐시 비우기 */
export function clearRelayMotionCache(): void {
  cache.clear();
}

function motionOf(load: (sid: string) => Promise<RunBundle>, sid: string): Promise<RelayMotion> {
  let p = cache.get(sid);
  if (!p) {
    p = load(sid).then(
      (b) => (b.program ? relayMotion(b.program.ast) : "unknown"),
      () => {
        // 실패는 남기지 않는다 — 다음 번에 다시 묻는다
        cache.delete(sid);
        return "unknown" as const;
      },
    );
    cache.set(sid, p);
  }
  return p;
}

export function useRelayMotions(
  load: ((sid: string) => Promise<RunBundle>) | null,
  sids: readonly (string | null | undefined)[],
  on: boolean,
): ReadonlyMap<string, RelayMotion> {
  const [out, setOut] = useState<ReadonlyMap<string, RelayMotion>>(() => new Map());
  const key = on && load ? [...new Set(sids.filter((s): s is string => !!s))].sort().join(",") : "";
  useEffect(() => {
    if (!key || !load) return;
    let alive = true;
    const ids = key.split(",");
    void Promise.all(ids.map((sid) => motionOf(load, sid).then((m) => [sid, m] as const))).then(
      (pairs) => {
        if (alive) setOut(new Map(pairs));
      },
    );
    return () => {
      alive = false;
    };
    // load 는 세션이 바뀔 때만 바뀐다 — 스냅샷 목록(key)이 바뀔 때 다시 본다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return out;
}
